import {
    Component,
    EventEmitter,
    ExistingProvider,
    forwardRef,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import {
    ControlValueAccessor,
    FormBuilder,
    FormGroup,
    NG_VALUE_ACCESSOR,
} from '@angular/forms';

export const TIMER_VALUE_ACCESSOR: ExistingProvider = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => CmpsEditableTimerComponent),
    multi: true,
};

interface TimeStruct {
    hours: string;
    minutes: string;
    seconds: string;
}

function pad(str: string, padStr: string, length: number): string {
    return (new Array(length + 1).join(padStr) + str).slice(-length);
}

function getStructFromCounter(counter: number): TimeStruct {
    const hours = Math.floor(counter / 3600);
    const minutes = Math.floor((counter % 3600) / 60);
    const seconds = Math.floor((counter % 3600) % 60);

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return {
        hours: pad(hours.toString(), '0', 2),
        minutes: pad(minutes.toString(), '0', 2),
        seconds: pad(seconds.toString(), '0', 2),
    } as TimeStruct;
}

function getCountFromTimeParts(h: number, m: number, s: number): number {
    const hours = !isNaN(h) ? h : 0;
    const minutes = !isNaN(m) ? m : 0;
    const seconds = !isNaN(s) ? s : 0;

    return hours * 3600 + minutes * 60 + seconds;
}

@Component({
    selector: '[cmpsEditableTimer]',
    template: `
        <form
            [formGroup]="timerForm"
            class="editable-timer"
            novalidate
        >
            <div
                formGroupName="timeStruct"
                novalidate
            >
                <input
                    type="number"
                    max="99"
                    min="0"
                    [readonly]="readonly"
                    (change)="onOverride()"
                    formControlName="hours"
                    (focus)="pause()"
                    (blur)="unpause()"
                />
                <span class="blinking-colon">:</span>

                <input
                    type="number"
                    max="59"
                    min="0"
                    [readonly]="readonly"
                    (change)="onOverride()"
                    formControlName="minutes"
                    (focus)="pause()"
                    (blur)="unpause()"
                />
                <span class="blinking-colon">:</span>

                <ng-container *ngIf="showSeconds">
                    <input
                        type="number"
                        max="59"
                        min="0"
                        [readonly]="readonly"
                        (change)="onOverride()"
                        formControlName="seconds"
                        (focus)="pause()"
                        (blur)="unpause()"
                    />
                </ng-container>
            </div>
        </form>
    `,
    providers: [TIMER_VALUE_ACCESSOR],
})
export class CmpsEditableTimerComponent
    implements OnInit, OnDestroy, OnChanges, ControlValueAccessor
{
    // .valueChanges() will stream continuously until paused or destroyed
    @Input() stopped; // optional override for pausing
    @Input() readonly;
    @Input() showHours;
    @Input() showMinutes;
    @Input() showSeconds;
    @Input() disabled;

    public timerForm: FormGroup;
    private timeStruct: TimeStruct;
    private counter: number;
    private timeoutId: any;
    private _paused: boolean;
    private _cacheCounter: number;

    @Output() timer: EventEmitter<number>;

    constructor(private readonly formBuilder: FormBuilder) {
        this.stopped = false; // optional override for pausing
        this.readonly = false;
        this.showHours = true;
        this.showMinutes = true;
        this.showSeconds = true;
        this.disabled = false;
        this._paused = false;
        this._cacheCounter = 0;
        this.counter = 0;
        this.timer = new EventEmitter<number>();

        this.setStruct();
        this.timerForm = this.createFormGroup(formBuilder);
    }

    ngOnInit(): void {
        if (!this.disabled) {
            this.start();
        }
    }

    ngOnDestroy(): void {
        this.stop();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnChanges(args: any) {
        // eslint-disable-next-line no-prototype-builtins
        if (args.hasOwnProperty('stopped')) {
            if (this.stopped === true) {
                this.stop();
            }
        }
    }

    // ControlValueAccessor interface methods
    // ----------------------------------------
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onChange = (_: any) => {
        /**/
    };

    onTouched: () => void;

    // counter as seconds
    writeValue(counter: any): void {
        if (typeof counter === 'number') {
            this.counter = counter;
        } else if (typeof counter === 'string') {
            this.counter = this._cacheCounter;
        } else {
            this.counter = 0;
        }
        this.tick();
    }

    registerOnChange(fn: (_: any) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(state: boolean): void {
        this.disabled = state;
    }
    // ----------------------------------------

    setStruct(): void {
        this.timeStruct = getStructFromCounter(this.counter);
    }

    tick(): void {
        this.setStruct();
        this.timerForm.setValue({
            timeStruct: {
                hours: this.timeStruct.hours,
                minutes: this.timeStruct.minutes,
                seconds: this.timeStruct.seconds,
            },
        });
    }

    start(): void {
        this.counter++;
        this.onChange(this.counter);
        this.tick();
        this.timeoutId = setTimeout(() => {
            this.start();
        }, 1000);
    }

    pause(): void {
        if (this.readonly) return;

        this._paused = true;
        clearTimeout(this.timeoutId);
    }

    unpause(): void {
        if (this.readonly) return;

        this._paused = false;
        this.start();
    }

    createFormGroup(formBuilder: FormBuilder): FormGroup {
        return formBuilder.group({
            timeStruct: formBuilder.group({
                hours: this.timeStruct.hours,
                minutes: this.timeStruct.minutes,
                seconds: this.timeStruct.seconds,
            }),
        });
    }

    stop(): void {
        this._paused = false;
        clearTimeout(this.timeoutId);
    }

    onOverride(): void {
        this._cacheCounter = this.counter; // cache current value right before overriding, in case of undo
        const _formval = this.timerForm.getRawValue();
        const _currStruct: TimeStruct = _formval.timeStruct;

        if (_currStruct) {
            let _h = parseInt(_currStruct.hours);
            let _m = parseInt(_currStruct.minutes);
            let _s = parseInt(_currStruct.seconds);

            if (isNaN(_h) || _h < 0) {
                _h = 0;
            }
            if (isNaN(_m) || _m < 0) {
                _m = 0;
            }
            if (isNaN(_s) || _s < 0) {
                _s = 0;
            }

            this.counter = getCountFromTimeParts(_h, _m, _s) || 0;
            this.onChange(this.counter); // in seconds
            this.tick();
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this.onTouched && this.onTouched();
        }
    }
}
